import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ListFooter, byLineNo } from '../ui';

// Stitching-lines Layer 2 — per-style LINE SETUP.
// A setup COPIES a sample_bulletin's operations into stations and binds each to a
// specific machine. Binding is a PLAN — only "Apply to floor" touches machine.line_id.
// Write-gated backend-side; UI hides write controls for non-managing roles.

const MANAGE_ROLES = ['admin', 'management', 'ppc_manager', 'production_incharge', 'production_supervisor'];
const STATUS_STYLE = {
  draft:    { bg: '#F7F6F3', c: '#6a6a6a', label: 'Draft' },
  active:   { bg: '#EAF7EE', c: '#166534', label: 'Active (applied)' },
  archived: { bg: '#eee', c: '#888', label: 'Archived' },
};
const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase' };
const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5' };
const mcNo = (m) => m.handwritten_mc_no || m.barcoded_mc_no || m.asset_tag;   // Layer-1 display priority

export default function LineSetup() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);

  const [setups, setSetups] = useState([]);
  const [lines, setLines] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [open, setOpen] = useState(null);          // the open setup detail { ...header, stations }
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ sample_bulletin_id: '', line_id: '', name: '' });
  const [machineOpts, setMachineOpts] = useState({});   // machine_type → [machines]
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const loadList = useCallback(() => api.get('/ppc/line-setups').then(r => setSetups(r.data.data || [])).catch(() => setSetups([])), []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    api.get('/ppc/lines-all').then(r => setLines((r.data.data || []).filter(l => l.is_active))).catch(() => {});
    api.get('/sample-bulletins').then(r => setBulletins(r.data.data || r.data || [])).catch(() => {});
  }, []);

  const openSetup = async (id) => {
    setErr('');
    const r = await api.get('/ppc/line-setups/' + id);
    const s = r.data.data; setOpen(s);
    // preload machine options for each distinct machine_type
    const types = [...new Set((s.stations || []).map(x => x.machine_type).filter(Boolean))];
    const opts = {};
    for (const t of types) {
      try { opts[t] = (await api.get('/ppc/line-setups/machines-for/' + encodeURIComponent(t))).data.data || []; }
      catch { opts[t] = []; }
    }
    setMachineOpts(opts);
  };

  const create = async () => {
    setErr(''); setBusy(true);
    try {
      const r = await api.post('/ppc/line-setups', createForm);
      setCreateModal(false); setCreateForm({ sample_bulletin_id: '', line_id: '', name: '' });
      await loadList(); openSetup(r.data.data.id);
    } catch (e) { setErr(e.response?.data?.error || 'Create failed'); }
    finally { setBusy(false); }
  };
  const bindStation = async (st, patch) => {
    try { await api.patch(`/ppc/line-setups/${open.id}/stations/${st.id}`, patch); openSetup(open.id); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  const clone = async () => { try { const r = await api.post(`/ppc/line-setups/${open.id}/clone`, {}); await loadList(); openSetup(r.data.data.id); } catch (e) { alert(e.response?.data?.error || 'Failed'); } };
  const apply = async () => {
    if (!window.confirm(`Apply setup to floor?\n\nThis sets every BOUND machine's line to "${open.line_no}" and status to running. This is the only action that moves physical machines.`)) return;
    try { const r = await api.post(`/ppc/line-setups/${open.id}/apply`, {}); alert(`✅ Applied — ${r.data.data.machines_applied} machine(s) moved to ${open.line_no} + set running.`); openSetup(open.id); loadList(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  const resync = async () => {
    if (!window.confirm('Re-pull operations from the source bulletin? Unbound stations are replaced; bound stations keep their machine but refresh op details.')) return;
    try { const r = await api.post(`/ppc/line-setups/${open.id}/resync`, {}); alert(`Re-synced: ${r.data.data.stations} stations, ${r.data.data.bound_kept} bound kept.${r.data.data.changes?.length ? '\n\nChanges:\n' + r.data.data.changes.join('\n') : ''}`); openSetup(open.id); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  const remove = async (s) => { if (!window.confirm(`Delete setup "${s.name}"? (Does not move any machines.)`)) return; try { await api.delete('/ppc/line-setups/' + s.id); if (open?.id === s.id) setOpen(null); loadList(); } catch (e) { alert(e.response?.data?.error || 'Failed'); } };

  const badge = (s) => { const t = STATUS_STYLE[s] || STATUS_STYLE.draft; return <span style={{ padding: '2px 9px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: t.bg, color: t.c }}>{t.label}</span>; };
  const totalSam = open ? (open.stations || []).reduce((s, x) => s + Number(x.sam || 0), 0) : 0;

  // ── DETAIL view ──
  if (open) {
    return (
      <div>
        <button onClick={() => setOpen(null)} style={{ padding: '6px 12px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginBottom: 12 }}>← Back to setups</button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#2D2C72' }}>{open.name} {badge(open.status)}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Style {open.style_no || '—'} · Line <strong>{open.line_no}</strong> · {(open.stations || []).length} operations · total SAM {totalSam.toFixed(3)}</div>
          </div>
          {canManage && <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={resync} style={{ padding: '7px 12px', background: '#EEEDF8', color: '#3B3A8F', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>⟳ Re-sync</button>
            <button onClick={clone} style={{ padding: '7px 12px', background: '#EEEDF8', color: '#3B3A8F', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>⧉ Clone</button>
            <button onClick={apply} style={{ padding: '7px 14px', background: '#166534', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>▶ Apply to floor</button>
          </div>}
        </div>
        {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
        <div style={{ border: '1px solid #eee', borderRadius: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F7F6F3' }}>{['#', 'Operation', 'Machine Type', 'Bound Machine', 'Operator', 'SAM'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {(open.stations || []).map(st => {
                const opts = machineOpts[st.machine_type] || [];
                const isManual = !st.machine_type || opts.length === 0;
                return (
                  <tr key={st.id}>
                    <td style={{ ...td, fontWeight: 700, color: '#888' }}>{st.sno}</td>
                    <td style={td}>{st.operation_name || '—'}</td>
                    <td style={td}><span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{st.machine_type || '—'}</span></td>
                    <td style={td}>
                      {isManual ? <span style={{ color: '#B45309', fontSize: 11 }}>no machine (manual)</span>
                        : canManage
                          ? <select value={st.machine_id || ''} onChange={e => bindStation(st, { machine_id: e.target.value || null })} style={{ ...inputStyle, width: 200, padding: '4px 8px' }}>
                              <option value="">— unbound —</option>
                              {opts.map(m => <option key={m.id} value={m.id}>{mcNo(m)}{m.line_id ? ` (on line)` : ''}</option>)}
                            </select>
                          : (st.machine_id ? (st.handwritten_mc_no || st.barcoded_mc_no || st.asset_tag) : '— unbound —')}
                    </td>
                    <td style={td}>
                      {canManage
                        ? <input value={st.operator_name || ''} onChange={e => bindStation(st, { operator_name: e.target.value })} style={{ ...inputStyle, width: 120, padding: '4px 8px' }} placeholder="—" />
                        : (st.operator_name || '—')}
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{st.sam != null ? Number(st.sam).toFixed(3) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: '#888' }}>Operations are a stable COPY from the bulletin — edits to the bulletin don't change this setup (use ⟳ Re-sync to re-pull). Binding a machine is a plan; only ▶ Apply to floor moves physical machines.</div>
      </div>
    );
  }

  // ── LIST view ──
  return (
    <div>
      {canManage && <div style={{ marginBottom: 12, textAlign: 'right' }}>
        <button onClick={() => { setErr(''); setCreateModal(true); }} style={{ padding: '7px 16px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>+ New Line Setup</button>
      </div>}
      <div style={{ border: '1px solid #eee', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#F7F6F3' }}>{['Setup', 'Style', 'Line', 'Ops', 'Bound', 'Total SAM', 'Status', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {setups.length === 0 ? <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 20 }}>No line setups yet.</td></tr>
              : setups.map(s => (
                <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => openSetup(s.id)}>
                  <td style={{ ...td, fontWeight: 700, color: '#3B3A8F' }}>{s.name}</td>
                  <td style={td}>{s.style_no || '—'}</td>
                  <td style={td}>{s.line_no}</td>
                  <td style={td}>{s.station_count}</td>
                  <td style={td}>{s.bound_count}/{s.station_count}</td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{Number(s.total_sam || 0).toFixed(3)}</td>
                  <td style={td}>{badge(s.status)}</td>
                  <td style={{ ...td, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    {canManage && <button onClick={() => remove(s)} style={{ padding: '3px 9px', background: '#FCEBEB', border: 'none', borderRadius: 4, color: '#A32D2D', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Delete</button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <ListFooter shown={setups.length} total={setups.length} noun="setups" />

      {createModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 480, maxWidth: '92vw' }}>
            <div style={{ background: '#2D2C72', padding: '14px 22px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>New Line Setup</h3></div>
            <div style={{ padding: 22 }}>
              {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
              <div style={{ marginBottom: 12 }}><label style={labelStyle}>Style (Sample Bulletin) *</label>
                <select style={inputStyle} value={createForm.sample_bulletin_id} onChange={e => setCreateForm(f => ({ ...f, sample_bulletin_id: e.target.value }))}>
                  <option value="">— select bulletin —</option>
                  {bulletins.map(b => <option key={b.id} value={b.id}>{b.style_no || b.bulletin_no || b.id.slice(0, 8)}{b.bulletin_no ? ` · ${b.bulletin_no}` : ''}</option>)}
                </select>
                <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>The bulletin's operation sequence is copied into stations.</div>
              </div>
              <div style={{ marginBottom: 12 }}><label style={labelStyle}>Line *</label>
                <select style={inputStyle} value={createForm.line_id} onChange={e => setCreateForm(f => ({ ...f, line_id: e.target.value }))}>
                  <option value="">— select line —</option>
                  {lines.slice().sort(byLineNo).map(l => <option key={l.id} value={l.id}>{l.line_no}</option>)}
                </select></div>
              <div style={{ marginBottom: 12 }}><label style={labelStyle}>Name (optional)</label><input style={inputStyle} value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="auto from style" /></div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setCreateModal(false)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button disabled={busy || !createForm.sample_bulletin_id || !createForm.line_id} onClick={create} style={{ padding: '8px 20px', background: (createForm.sample_bulletin_id && createForm.line_id) ? '#3B3A8F' : '#ccc', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>{busy ? 'Creating…' : 'Create'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
